// ============================================================
// previewPolicy — pure, no-I/O policy projection for the Owner Preview
// console.
//
// This is a PREVIEW, never a real session: it renders what a persona WOULD
// see, computed from canonical reference data that already exists in the
// repo — it does not read or write any real resource content, does not
// mutate anything, and cannot be satisfied by an external request (there is
// no route here at all, only pure functions).
//
// Canonical sources reused verbatim, not duplicated:
//   - PERMISSION_MATRIX (packages/square-shell/src/routes/Projects/accessControl.ts)
//     — the real role x resource grant grid already rendered by the
//     Commandery Access Control panel.
//   - MODULE_CATALOG / CLIENT_VISIBILITY_RULES (../portal-factory/portalFactory)
//     — the real portal module registry + the "nothing is client-visible by
//     default" posture already governing Portal Factory.
//
// Canonical role reality (see discovery): only two role vocabularies exist —
// operator/org RBAC (olv_roles, off-limits to this preview) and
// sq_project_members.role IN ('operator','client_owner','client_collaborator',
// 'viewer'). Student, Trainee, and Test User have NO canonical backing
// anywhere in the schema or docs. This module never invents a persisted role:
// every PREVIEW_PERSONA below is a presentation-only profile that reuses an
// EXISTING grant row (client_owner / client_collaborator / viewer) — Team
// Member is the one derived profile (see canonicalNote), narrowed from
// client_owner so it never inherits owner-tier authority.
// ============================================================

import { commandCenterPath } from '../../commandCenterRoutes'
import { PERMISSION_MATRIX, RESOURCES, type Resource, type Role, type Grant } from '../../../Projects/accessControl'
import { MODULE_CATALOG, CLIENT_VISIBILITY_RULES, type ModuleCatalogEntry } from '../portal-factory/portalFactory'

// Kept in lockstep with router.tsx (which hardcodes the literal path, per the
// existing portalFactory.ts convention) — exported so tests and any future
// deep-link helper never duplicate the magic string.
export const OWNER_PREVIEW_ROOT = commandCenterPath('olivium/portal-factory/preview')

export type PreviewPersonaKey = 'team_member' | 'collaborator' | 'client' | 'student' | 'trainee' | 'test_user'

export interface PreviewPersona {
  key: PreviewPersonaKey
  label: string
  /** The existing sq_project_members role this profile is based on. */
  basedOnRole: Role
  /** True when the grant row is DERIVED (narrowed), not a direct 1:1 reuse. */
  derived: boolean
  /** Explains the mapping honestly — shown in the console, never hidden. */
  canonicalNote: string
}

export const PREVIEW_PERSONAS: PreviewPersona[] = [
  {
    key: 'team_member',
    label: 'Team Member',
    basedOnRole: 'client_owner',
    derived: true,
    canonicalNote:
      "No canonical role matches \"team member\" — sq_project_members.role='operator' is the owner-equivalent within a project, so previewing as operator would leak owner-tier authority. This profile narrows client_owner: approvals and receipts are removed.",
  },
  {
    key: 'collaborator',
    label: 'Collaborator',
    basedOnRole: 'client_collaborator',
    derived: false,
    canonicalNote: "Maps 1:1 onto the existing sq_project_members.role = 'client_collaborator'.",
  },
  {
    key: 'client',
    label: 'Client',
    basedOnRole: 'client_owner',
    derived: false,
    canonicalNote: "Maps 1:1 onto the existing sq_project_members.role = 'client_owner'.",
  },
  {
    key: 'student',
    label: 'Student',
    basedOnRole: 'viewer',
    derived: true,
    canonicalNote:
      "No canonical role exists for \"student\" anywhere in the schema or docs. Presentation-only profile derived verbatim from the existing read-only 'viewer' grant.",
  },
  {
    key: 'trainee',
    label: 'Trainee',
    basedOnRole: 'viewer',
    derived: true,
    canonicalNote:
      "No canonical role exists for \"trainee\" anywhere in the schema or docs. Presentation-only profile derived verbatim from the existing read-only 'viewer' grant.",
  },
  {
    key: 'test_user',
    label: 'Test User',
    basedOnRole: 'viewer',
    derived: true,
    canonicalNote:
      "No canonical role exists for \"test user\" anywhere in the schema or docs. Presentation-only profile derived verbatim from the existing read-only 'viewer' grant — intended for short-lived, minimal-blast-radius verification access.",
  },
]

export function previewPersona(key: PreviewPersonaKey): PreviewPersona {
  const p = PREVIEW_PERSONAS.find((x) => x.key === key)
  if (!p) throw new Error(`unknown preview persona: ${key}`)
  return p
}

/** Resources removed from the Team Member derived profile (see canonicalNote above). */
const TEAM_MEMBER_REMOVED_RESOURCES = new Set<Resource>(['approvals', 'receipts'])

/** The grant a persona would have for a resource — reuses PERMISSION_MATRIX verbatim except the one derived profile. */
export function grantForPersona(personaKey: PreviewPersonaKey, resource: Resource): Grant {
  const persona = previewPersona(personaKey)
  if (persona.key === 'team_member' && TEAM_MEMBER_REMOVED_RESOURCES.has(resource)) return 'none'
  return PERMISSION_MATRIX[persona.basedOnRole][resource]
}

// ---- preview state vocabulary ---------------------------------------------

export type PreviewState = 'hidden' | 'summary_only' | 'read_only' | 'constrained_action' | 'blocked'

export const PREVIEW_STATE_META: Record<PreviewState, { label: string }> = {
  hidden: { label: 'Hidden' },
  summary_only: { label: 'Summary only' },
  read_only: { label: 'Read only' },
  constrained_action: { label: 'Constrained action' },
  blocked: { label: 'Blocked' },
}

/** A grant always resolves to exactly one of the five allowed preview states. */
export function stateForGrant(grant: Grant): { state: PreviewState; reason: string } {
  switch (grant) {
    case 'full':
      return { state: 'constrained_action', reason: 'Full collaboration grant — constrained to this project, never to the owner-wide surface.' }
    case 'read':
      return { state: 'read_only', reason: 'Read-only grant for this resource.' }
    case 'request':
      return { state: 'constrained_action', reason: 'Can request; cannot execute directly.' }
    case 'planned':
      return { state: 'blocked', reason: 'Planned — not yet available.' }
    case 'none':
    default:
      return { state: 'blocked', reason: 'No grant for this resource under this persona.' }
  }
}

// ---- gate — invitation / agreement / expiration / revocation --------------
//
// These are OWNER-CHOSEN SIMULATED inputs (not real sq_project_access_invites
// or agreement rows — no such agreement system exists in the repo yet; see
// the NDA readiness audit). The gate is evaluated BEFORE any per-resource
// grant: an expired, revoked, suspended, not-yet-started, or NDA-incomplete
// access blocks everything, regardless of the persona's grants.

export interface GateState {
  started: boolean
  expired: boolean
  revoked: boolean
  suspended: boolean
  ndaRequired: boolean
  ndaComplete: boolean
}

export function defaultGate(): GateState {
  return { started: true, expired: false, revoked: false, suspended: false, ndaRequired: false, ndaComplete: true }
}

/** Returns a blocking reason if the gate denies access outright, else null. */
export function evaluateGate(gate: GateState): { reason: string } | null {
  if (gate.revoked) return { reason: 'Access has been revoked.' }
  if (gate.suspended) return { reason: 'Access is suspended.' }
  if (gate.expired) return { reason: 'Access has expired.' }
  if (!gate.started) return { reason: 'Access has not started yet.' }
  if (gate.ndaRequired && !gate.ndaComplete) return { reason: 'An NDA is required and has not been completed.' }
  return null
}

// ---- module projection -----------------------------------------------------
//
// Every module in MODULE_CATALOG is classified into exactly one of:
//   - alwaysHidden   — internal-only per CLIENT_VISIBILITY_RULES (never
//                      shown to ANY non-operator persona, regardless of grant)
//   - notBuilt       — the module's own catalog status is BACKEND_DEFERRED /
//                      INTEGRATION_DEFERRED (blocked regardless of grant —
//                      never claim a persona can use something that doesn't
//                      exist yet)
//   - resource       — maps onto a real accessControl.ts Resource; state is
//                      derived from the persona's grant for that resource

type ModulePolicy = { kind: 'always_hidden'; reason: string } | { kind: 'not_built' } | { kind: 'resource'; resource: Resource }

const MODULE_POLICY: Record<string, ModulePolicy> = {
  overview: { kind: 'resource', resource: 'workspace' },
  actions: { kind: 'resource', resource: 'tasks' },
  notes: { kind: 'always_hidden', reason: CLIENT_VISIBILITY_RULES.find((r) => r.kind === 'private_notes')!.rule },
  blockers: { kind: 'always_hidden', reason: CLIENT_VISIBILITY_RULES.find((r) => r.kind === 'raw_blockers')!.rule },
  receipts: { kind: 'resource', resource: 'receipts' },
  settings: { kind: 'always_hidden', reason: 'Portal settings are operator-only configuration — never previewed as a non-operator persona.' },
  tasks: { kind: 'resource', resource: 'tasks' },
  communications: { kind: 'resource', resource: 'messages' },
  team: { kind: 'always_hidden', reason: 'Project team/role administration is operator-only — never previewed as a non-operator persona.' },
  daily_meetings: { kind: 'resource', resource: 'calendar' },
  files: { kind: 'resource', resource: 'files' },
  client_access: { kind: 'always_hidden', reason: 'This module describes the (deferred) external-access bridge itself — not a resource any persona previews.' },
  documents: { kind: 'resource', resource: 'deliverables' },
  approvals: { kind: 'resource', resource: 'approvals' },
  intake: { kind: 'always_hidden', reason: 'Client intake is an operator-only acquisition function — never previewed as a non-operator persona.' },
  campaigns: { kind: 'resource', resource: 'deliverables' },
}

export interface ProjectedRow {
  key: string
  label: string
  state: PreviewState
  reason: string
}

/** Projects one MODULE_CATALOG entry for a persona + gate. Gate wins over grant; catalog "not built" wins over grant. */
export function projectModule(entry: ModuleCatalogEntry, personaKey: PreviewPersonaKey, gate: GateState): ProjectedRow {
  const gateBlock = evaluateGate(gate)
  if (gateBlock) return { key: entry.key, label: entry.label, state: 'blocked', reason: gateBlock.reason }

  const policy = MODULE_POLICY[entry.key]
  if (!policy) return { key: entry.key, label: entry.label, state: 'blocked', reason: 'No preview policy defined for this module yet.' }

  if (policy.kind === 'always_hidden') return { key: entry.key, label: entry.label, state: 'hidden', reason: policy.reason }

  if (entry.status === 'BACKEND_DEFERRED' || entry.status === 'INTEGRATION_DEFERRED') {
    return { key: entry.key, label: entry.label, state: 'blocked', reason: `Not built yet (${entry.status}) — no persona can use this module today.` }
  }

  if (policy.kind === 'not_built') return { key: entry.key, label: entry.label, state: 'blocked', reason: 'Not built yet.' }

  const grant = grantForPersona(personaKey, policy.resource)
  const { state, reason } = stateForGrant(grant)
  return { key: entry.key, label: entry.label, state, reason }
}

export function projectAllModules(personaKey: PreviewPersonaKey, gate: GateState): ProjectedRow[] {
  return MODULE_CATALOG.map((m) => projectModule(m, personaKey, gate))
}

/** Projects the plain resource grid (accessControl.ts RESOURCES) for a persona + gate — independent of the module catalog. */
export function projectResource(resource: Resource, personaKey: PreviewPersonaKey, gate: GateState): ProjectedRow {
  const gateBlock = evaluateGate(gate)
  if (gateBlock) return { key: resource, label: resource, state: 'blocked', reason: gateBlock.reason }
  const grant = grantForPersona(personaKey, resource)
  const { state, reason } = stateForGrant(grant)
  return { key: resource, label: resource, state, reason }
}

export function projectAllResources(personaKey: PreviewPersonaKey, gate: GateState): ProjectedRow[] {
  return RESOURCES.map((r) => projectResource(r, personaKey, gate))
}
